import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Pagination,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Assignment as AssignIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Business as ServiceIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const ReviewManagement = () => {
  const { currentUser, apiCall } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [pendingListings, setPendingListings] = useState({ jobs: [], services: [] });
  const [myAssignments, setMyAssignments] = useState({ jobs: [], services: [] });
  const [reviewStats, setReviewStats] = useState({});
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalJobs: 0,
    totalServices: 0
  });

  // Add missing state variables
  const [currentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  // Removed unused filterType to satisfy lint
  const [filterStatus] = useState('under_review');

  // Dialog states
  const [viewDialog, setViewDialog] = useState({ open: false, listing: null, type: null });
  const [assignDialog, setAssignDialog] = useState({ open: false, listing: null, type: null });
  const [reviewDialog, setReviewDialog] = useState({ 
    open: false, 
    listing: null, 
    type: null, 
    action: null 
  });
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  

  const fetchPendingListings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall('/review/pending', {
        method: 'GET',
        params: {
          page: pagination.currentPage,
          limit: 10,
          type: 'all'
        }
      });

      if (response.success) {
        setPendingListings(response.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      showAlert('Failed to fetch pending listings', 'error');
    } finally {
      setLoading(false);
    }
  }, [apiCall, pagination.currentPage]);

  

  const fetchMyAssignments = useCallback(async () => {
    try {
      const response = await apiCall('/review/my-assignments', {
        method: 'GET',
        params: {
          page: currentPage,
          limit: itemsPerPage,
          status: filterStatus
        }
      });

      if (response.success) {
        setMyAssignments(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    }
  }, [apiCall, currentPage, itemsPerPage, filterStatus]);

  const fetchReviewStats = useCallback(async () => {
    try {
      const response = await apiCall('/review/stats', {
        method: 'GET'
      });

      if (response.success) {
        setReviewStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    }
  }, [apiCall]);

  const fetchReviewers = useCallback(async () => {
    try {
      const response = await apiCall('/admin-management', {
        method: 'GET',
        params: {
          role: 'review_officer,admin,super_admin'
        }
      });

      if (response.success) {
        setReviewers(response.data.admins || []);
      }
    } catch (error) {
      console.error('Failed to fetch reviewers:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchPendingListings();
    fetchMyAssignments();
    fetchReviewStats();
    fetchReviewers();
  }, [activeTab, pagination.currentPage, fetchPendingListings, fetchMyAssignments, fetchReviewStats, fetchReviewers]);

  const handleAssignReviewer = async () => {
    if (!selectedReviewer) {
      showAlert('Please select a reviewer', 'error');
      return;
    }

    try {
      const response = await apiCall('/review/assign', {
        method: 'POST',
        body: JSON.stringify({
          reviewId: assignDialog.listing._id,
          reviewerId: selectedReviewer
        })
      });

      if (response.success) {
        showAlert('Reviewer assigned successfully', 'success');
        setAssignDialog({ open: false, listing: null, type: null });
        setSelectedReviewer('');
        fetchPendingListings();
      }
    } catch (error) {
      showAlert('Failed to assign reviewer', 'error');
    }
  };

  const handleReviewAction = async () => {
    if (reviewDialog.action === 'reject' && !reviewComment.trim()) {
      showAlert('Please provide a rejection comment', 'error');
      return;
    }

    try {
      const endpoint = `/review/${reviewDialog.action}`;
      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          reviewId: reviewDialog.review._id,
          notes: reviewDialog.notes
        })
      });

      if (response.success) {
        showAlert(`Listing ${reviewDialog.action}d successfully`, 'success');
        setReviewDialog({ open: false, listing: null, type: null, action: null });
        setReviewComment('');
        fetchPendingListings();
        fetchMyAssignments();
      }
    } catch (error) {
      showAlert(`Failed to ${reviewDialog.action} listing`, 'error');
    }
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 5000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'under_review': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const canAssignReviewer = () => {
    // Super admin has unrestricted access (GodMode)
    if (currentUser?.role === 'super_admin') return true;
    return ['super_admin', 'admin'].includes(currentUser?.role);
  };

  const canReviewListing = (listing) => {
    // Super admin has unrestricted access (GodMode)
    if (currentUser?.role === 'super_admin') return true;
    if (['super_admin', 'admin'].includes(currentUser?.role)) return true;
    return listing.assignedReviewer?._id === currentUser?.id;
  };

  const renderStatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending Jobs
            </Typography>
            <Typography variant="h4">
              {reviewStats.jobs?.pending || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending Services
            </Typography>
            <Typography variant="h4">
              {reviewStats.services?.pending || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Under Review
            </Typography>
            <Typography variant="h4">
              {(reviewStats.jobs?.under_review || 0) + (reviewStats.services?.under_review || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Approved Today
            </Typography>
            <Typography variant="h4">
              {(reviewStats.jobs?.approved || 0) + (reviewStats.services?.approved || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderListingTable = (listings, type) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>{type === 'job' ? 'Employer' : 'Provider'}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Assigned Reviewer</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {listings.map((listing) => (
            <TableRow key={listing._id}>
              <TableCell>
                <Box display="flex" alignItems="center">
                  {type === 'job' ? <WorkIcon sx={{ mr: 1 }} /> : <ServiceIcon sx={{ mr: 1 }} />}
                  {listing.title}
                </Box>
              </TableCell>
              <TableCell>
                {type === 'job' ? listing.employer?.name : listing.provider?.name}
              </TableCell>
              <TableCell>
                <Chip 
                  label={listing.reviewStatus} 
                  color={getStatusColor(listing.reviewStatus)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {listing.assignedReviewer ? (
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                    {listing.assignedReviewer.name}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Unassigned
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {new Date(listing.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Box display="flex" gap={1}>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => setViewDialog({ open: true, listing, type })}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  
                  {canAssignReviewer() && !listing.assignedReviewer && (
                    <Tooltip title="Assign Reviewer">
                      <IconButton
                        size="small"
                        onClick={() => setAssignDialog({ open: true, listing, type })}
                      >
                        <AssignIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {canReviewListing(listing) && listing.reviewStatus === 'under_review' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => setReviewDialog({ 
                            open: true, 
                            listing, 
                            type, 
                            action: 'approve' 
                          })}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setReviewDialog({ 
                            open: true, 
                            listing, 
                            type, 
                            action: 'reject' 
                          })}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Request Changes">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => setReviewDialog({ 
                            open: true, 
                            listing, 
                            type, 
                            action: 'request-changes' 
                          })}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Review Management
      </Typography>

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {renderStatsCards()}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Pending Listings" />
          <Tab label="My Assignments" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeTab === 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Jobs ({pendingListings.jobs?.length || 0})
                  </Typography>
                  {renderListingTable(pendingListings.jobs || [], 'job')}
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Services ({pendingListings.services?.length || 0})
                  </Typography>
                  {renderListingTable(pendingListings.services || [], 'service')}
                </>
              )}

              {activeTab === 1 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    My Job Assignments ({myAssignments.jobs?.length || 0})
                  </Typography>
                  {renderListingTable(myAssignments.jobs || [], 'job')}
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    My Service Assignments ({myAssignments.services?.length || 0})
                  </Typography>
                  {renderListingTable(myAssignments.services || [], 'service')}
                </>
              )}

              {pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={(e, page) => setPagination(prev => ({ ...prev, currentPage: page }))}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* View Listing Dialog */}
      <Dialog 
        open={viewDialog.open} 
        onClose={() => setViewDialog({ open: false, listing: null, type: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {viewDialog.type === 'job' ? 'Job' : 'Service'} Details
        </DialogTitle>
        <DialogContent>
          {viewDialog.listing && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {viewDialog.listing.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {viewDialog.listing.description}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status:</Typography>
                  <Chip 
                    label={viewDialog.listing.reviewStatus} 
                    color={getStatusColor(viewDialog.listing.reviewStatus)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Created:</Typography>
                  <Typography variant="body2">
                    {new Date(viewDialog.listing.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, listing: null, type: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Reviewer Dialog */}
      <Dialog 
        open={assignDialog.open} 
        onClose={() => setAssignDialog({ open: false, listing: null, type: null })}
      >
        <DialogTitle>Assign Reviewer</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Reviewer</InputLabel>
            <Select
              value={selectedReviewer}
              onChange={(e) => setSelectedReviewer(e.target.value)}
              label="Select Reviewer"
            >
              {reviewers.map((reviewer) => (
                <MenuItem key={reviewer._id} value={reviewer._id}>
                  {reviewer.name} ({reviewer.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, listing: null, type: null })}>
            Cancel
          </Button>
          <Button onClick={handleAssignReviewer} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Action Dialog */}
      <Dialog 
        open={reviewDialog.open} 
        onClose={() => setReviewDialog({ open: false, listing: null, type: null, action: null })}
      >
        <DialogTitle>
          {reviewDialog.action === 'approve' && 'Approve Listing'}
          {reviewDialog.action === 'reject' && 'Reject Listing'}
          {reviewDialog.action === 'request-changes' && 'Request Changes'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              reviewDialog.action === 'approve' ? 'Approval Comment (Optional)' :
              reviewDialog.action === 'reject' ? 'Rejection Reason (Required)' :
              'Change Request Details (Required)'
            }
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            sx={{ mt: 2 }}
            required={reviewDialog.action !== 'approve'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog({ open: false, listing: null, type: null, action: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleReviewAction} 
            variant="contained"
            color={
              reviewDialog.action === 'approve' ? 'success' :
              reviewDialog.action === 'reject' ? 'error' : 'warning'
            }
          >
            {reviewDialog.action === 'approve' && 'Approve'}
            {reviewDialog.action === 'reject' && 'Reject'}
            {reviewDialog.action === 'request-changes' && 'Request Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewManagement;